import { Button } from '@/components/ui';
import { cx } from '@/lib/cx';

/**
 * THE ACCOUNTS DASHBOARD'S "what is this list narrowed to" BAR — 2026-09-14,
 * owner's instruction that pressing a stat card should say so prominently.
 *
 * A stat card press already filtered the list; what it did not do was SAY so
 * anywhere a reader looks. The whole signal was a 2px accent rule on the card and
 * a generic *"1 filter applied"* line inside the filter bank, below the fold on a
 * narrow screen and silent about WHICH card. This bar is the missing sentence,
 * placed where the rows are.
 *
 * ⛔ **IT SAYS "Filtered by", NEVER "Showing".** The same component renders on the
 * Google Sheets view, where the frame is somebody else's cross-origin document and
 * **this app cannot know what it is displaying** — `SheetFilterShell`'s own status
 * line exists for exactly that, and it is the authority there. "Showing" would be a
 * claim about the rectangle below; "Filtered by" is a claim about the filter this
 * page holds, which is true on both views. **One wording for both, so the two
 * cannot drift into disagreeing about what the reader is looking at.**
 *
 * ⚠️ **IT IS NOT HELPER TEXT AND A SWEEP MUST NOT TAKE IT.** The no-helper-text
 * prohibition forbids prose attached to a CONTROL. This states which of several
 * populations is on screen — a fact the reader cannot otherwise see once the card
 * row has scrolled away, and the one that explains why a card reading 41 sits over
 * a table of 6. It is the documented exception, not a carve-out from it.
 *
 * ⚠️ **NOT RENDERED AT ALL WHEN NOTHING IS NARROWING.** An "all" card — Future
 * Billables' *Planned* — is the section unfiltered, so a bar announcing it would
 * be a sentence about the absence of a filter, which is noise on every ordinary
 * arrival. The call site passes `null` there; see `STAT_CARD_FILTER`.
 */

/**
 * The tone the bar is drawn in.
 *
 * ⚠️ **IT FOLLOWS THE CARD, on the owner's decision** — the two overdue cards
 * already render their figure in the danger tone, so a neutral bar over a
 * deliberately alarming card would put the alarm and the explanation in two
 * different registers. Every other card is `neutral`.
 *
 * ⚠️ *SUPERSEDED (UI rework, 2026-10): the non-danger tone was `accent`, an
 * `accent-brand` band.* The accent is now rationed to primary actions, the
 * active nav row and focus, so the ordinary bar is a quiet neutral band.
 */
export type AccountsFilterSummaryTone = 'neutral' | 'danger';

/**
 * ⚠️ **WHOLE CLASS LITERALS PER TONE, NEVER COMPOSED.** Tailwind scans source
 * text, so a class built as `` `border-${tone}-border` `` emits no CSS at all —
 * the bar would render with no border and no ground, which reads as a deliberate
 * borderless design rather than as missing styles.
 *
 */
const TONE: Readonly<Record<AccountsFilterSummaryTone, { box: string; mark: string }>> = {
  neutral: {
    box: 'border-border bg-surface-sunken text-fg',
    mark: 'text-fg-muted',
  },
  danger: {
    box: 'border-danger-border bg-danger-soft text-danger',
    mark: 'text-danger',
  },
};

/**
 * The funnel, drawn here rather than imported.
 *
 * ⚠️ `AccountsDashboardPage` has a `FilterIcon` of its own and it is MODULE-LOCAL
 * to that file. Exporting it to share one path would reshape a 7,500-line module
 * for a decoration; the path is `aria-hidden` and carries no meaning, so a second
 * copy costs nothing that matters. ⛔ **It is therefore not a deploy marker** —
 * a grep for this path finds two files and proves nothing about either.
 */
function FunnelMark({ className }: { className: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={cx('mt-0.5 h-4 w-4 shrink-0', className)}
    >
      <path d="M3 4h18l-7 8v6l-4 2v-8Z" />
    </svg>
  );
}

/**
 * *"company"*, *"company and due date"*, *"company, due date and currency"*.
 *
 * ⚠️ **An Oxford-comma-free English list, written out rather than `join(', ')`.**
 * The list is read aloud as part of one sentence, and *"company, due date"* reads
 * as a truncation — which is exactly the wrong impression under a bar whose job is
 * to account for every narrowing in force.
 */
function listSentence(items: readonly string[]): string {
  if (items.length <= 1) return items[0] ?? '';
  return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
}

export function AccountsFilterSummaryBar({
  cardLabel,
  tone,
  alsoFiltered,
  onClear,
}: {
  /**
   * The pressed card's own label, passed in rather than looked up.
   *
   * ⚠️ **`AccountsStatCards` OWNS THE WORDING** and hands it over through
   * `accountsStatLabel()`. A second spelling here is the one that would disagree
   * after the next relabel — and these labels have been argued over line by line
   * (*"Accepted, not yet invoiced"*, never *"not yet billed"*), so a paraphrase
   * would quietly make a different claim about the population.
   */
  cardLabel: string;
  tone: AccountsFilterSummaryTone;
  /**
   * Every OTHER narrowing in force on this section, already worded as nouns.
   *
   * 🔴 **THIS IS WHAT ACCOUNTS FOR THE FIGURE GAP, and the gap is real rather
   * than a fault.** The cards count the whole business — `/accounts/stats` takes
   * no parameters at all — while the table below is narrowed by whatever else the
   * reader has set, so *"Invoices pending payment: 41"* legitimately sits over a
   * table of 6. Naming the other filters is what turns that from an apparent
   * arithmetic error into an answered question. ⛔ **Do not close the gap by
   * making the cards follow the filters**: that needs a per-filter aggregate this
   * API does not publish, and deriving one is a request per company against a
   * rate limit shared across the office.
   */
  alsoFiltered: readonly string[];
  /**
   * Clears THIS CARD'S filter and nothing else — owner's decision.
   *
   * ⚠️ **Identical to pressing the lit card again**, deliberately: two controls
   * with one meaning. A Clear that also dropped the reader's company or date
   * filter would discard a narrowing they set separately and never asked to lose,
   * which is the difference between this and the section chip's `setSection`.
   */
  onClear: () => void;
}): React.JSX.Element {
  const { box, mark } = TONE[tone];

  return (
    /*
     * ⚠️ **`role="status"` so the change is ANNOUNCED.** The card's own
     * `aria-pressed` says a button became pressed; it does not say what the list
     * below now holds, and a sighted reader gets that from a bar appearing where
     * no bar was. This is the same fact, delivered to the reader who cannot see
     * it appear. It is not `alert`: nothing here is a problem, including on the
     * danger tone, which reports an alarming POPULATION and not an error.
     */
    <div
      role="status"
      className={cx(
        'flex flex-wrap items-start gap-x-3 gap-y-2 border-b px-4 py-2.5 text-xs',
        box,
      )}
    >
      <FunnelMark className={mark} />

      <p className="m-0 min-w-0 flex-1 [overflow-wrap:anywhere]">
        {/*
         * ⚠️ **The card's name carries the WEIGHT, not the lead-in.** A reader
         * scanning this bar is looking for which population, and "Filtered by" is
         * the same three words on every render.
         */}
        Filtered by <span className="font-semibold">{cardLabel}</span>
        {alsoFiltered.length > 0 && (
          <span className="opacity-90"> · also filtered by {listSentence(alsoFiltered)}</span>
        )}
      </p>

      {/*
       * ⚠️ **`ghost`, not `secondary`.** The bar is already a filled, bordered
       * band; a second bordered box inside it reads as two nested objects. The
       * word is "Clear" rather than "Clear filter" because the bar directly above
       * it has just named which one.
       */}
      <Button variant="ghost" size="sm" className="shrink-0" onClick={onClear}>
        Clear
      </Button>
    </div>
  );
}
